<template>
  <div class="rights-workbench">
    <Title title="版权与展示控制" />

    <div class="page-intro">
      <div>
        <h2>先审核高风险资源，再逐步扩大安全资源的展示</h2>
        <p>未审核资源继续沿用当前展示。只有明确启用“普通限制、仅搜索可见、完全隐藏”等策略后，客户端行为才会改变。</p>
      </div>
      <div class="intro-actions">
        <el-button icon="el-icon-folder-opened" @click="recordManagerVisible = true">版权档案库</el-button>
        <el-button icon="el-icon-refresh" :loading="loading" @click="loadList(false)">刷新</el-button>
      </div>
    </div>

    <el-row :gutter="12" class="summary-row">
      <el-col v-for="card in summaryCards" :key="card.key" :span="4">
        <button type="button" class="summary-card" :class="card.type" @click="applySummaryFilter(card)">
          <span>{{ card.label }}</span>
          <strong>{{ card.value }}</strong>
          <small>{{ card.description }}</small>
        </button>
      </el-col>
    </el-row>

    <el-card shadow="never" class="filter-card">
      <div class="filters">
        <el-input
          v-model.trim="filters.search_name"
          clearable
          prefix-icon="el-icon-search"
          placeholder="搜索音效名称"
          @keyup.enter.native="loadList()"
          @clear="loadList()" />
        <el-select v-model="filters.verification_status" clearable placeholder="审核进度" @change="loadList()">
          <el-option
            v-for="item in options.verification_statuses"
            :key="item.value"
            :label="item.label"
            :value="item.value" />
        </el-select>
        <el-select v-model="filters.risk_level" clearable placeholder="风险等级" @change="loadList()">
          <el-option
            v-for="item in riskFilterOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value" />
        </el-select>
        <el-select v-model="filters.preset_code" clearable placeholder="展示策略" @change="loadList()">
          <el-option
            v-for="item in options.presets"
            :key="item.value"
            :label="item.label"
            :value="item.value" />
        </el-select>
        <el-button type="primary" icon="el-icon-search" @click="loadList()">查询</el-button>
        <el-button @click="resetFilters">重置</el-button>
      </div>
    </el-card>

    <el-card shadow="never" class="table-card">
      <Player ref="player" :currSrc="currSrc" />
      <div v-if="previewSoundName" class="preview-source">
        <i class="el-icon-headset"></i>
        正在试听：{{ previewSoundName }}
        <el-tag size="mini">{{ previewSoundSourceType }}</el-tag>
        <span>{{ previewSoundAddress }}</span>
      </div>

      <el-table :data="list" v-loading="loading" style="width:100%">
        <el-table-column prop="name" label="资源" min-width="220">
          <template slot-scope="scope">
            <div class="sound-name">{{ scope.row.name }}</div>
            <div class="sound-meta">ID {{ scope.row.id }} · {{ scope.row.category_name || '未分类' }}</div>
          </template>
        </el-table-column>
        <el-table-column label="版权情况" width="160">
          <template slot-scope="scope">
            <span>{{ optionLabel(options.rights_statuses, scope.row.rights_status, '版权情况未知') }}</span>
          </template>
        </el-table-column>
        <el-table-column label="审核" width="120">
          <template slot-scope="scope">
            <el-tag size="small" :type="verificationType(scope.row.verification_status)">
              {{ optionLabel(options.verification_statuses, scope.row.verification_status, '未审核') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="风险" width="110">
          <template slot-scope="scope">
            <el-tag size="small" :type="riskType(scope.row.risk_level)" effect="plain">
              {{ optionLabel(options.risk_levels, scope.row.risk_level, '未判断') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="展示策略" width="150">
          <template slot-scope="scope">
            <el-tag size="small" :type="presetType(scope.row.preset_code)">
              {{ optionLabel(options.presets, scope.row.preset_code, '沿用当前展示') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="原状态" width="145">
          <template slot-scope="scope">
            <span class="original-state">
              {{ scope.row.status === 1 ? '已启用' : '已停用' }}
              ·
              {{ scope.row.recommend === 1 ? '最近更新' : '不进最近' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="190" fixed="right">
          <template slot-scope="scope">
            <el-button
              size="mini"
              icon="el-icon-video-play"
              :disabled="!canPreview(scope.row)"
              @click="playSound(scope.row)">
              试听
            </el-button>
            <el-button size="mini" type="primary" plain icon="el-icon-edit" @click="editSound(scope.row)">
              维护
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        class="pagination"
        background
        :current-page="page.page_num"
        :page-size="page.page_size"
        :page-sizes="[10, 20, 50, 100]"
        :total="page.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handlePageSizeChange"
        @current-change="handlePageChange" />
    </el-card>

    <el-drawer
      :visible.sync="drawerVisible"
      :title="drawerTitle"
      size="72%"
      :destroy-on-close="true"
      custom-class="rights-drawer">
      <div class="drawer-body">
        <SoundRightsAssignmentEditor
          v-if="drawerVisible && currentSoundId"
          :sound-id="currentSoundId"
          @saved="handleEditorSaved" />
      </div>
    </el-drawer>

    <el-dialog
      title="版权档案库"
      :visible.sync="recordManagerVisible"
      width="92%"
      top="4vh"
      :close-on-click-modal="false"
      append-to-body>
      <SoundRightsRecordManager v-if="recordManagerVisible" @record-saved="handleRecordSaved" />
    </el-dialog>
  </div>
</template>

<script>
import Title from '@/views/sounds/list/components/Title'
import Player from '@/components/Player.vue'
import SoundRightsAssignmentEditor from '@/components/sounds/SoundRightsAssignmentEditor.vue'
import SoundRightsRecordManager from '@/components/sounds/SoundRightsRecordManager.vue'
import apis from '@/api/index'
import {
  buildSoundPreview,
  canPreviewSound,
  exposurePresetTagType,
  optionLabel,
  riskTagType,
  verificationTagType
} from '@/lib/soundAdminUi'

export default {
  name: 'SoundRightsWorkbench',
  components: {
    Title,
    Player,
    SoundRightsAssignmentEditor,
    SoundRightsRecordManager
  },
  /**
   * 创建版权维护工作台的筛选、分页和试听状态。
   *
   * @returns {Object}
   */
  data() {
    return {
      loading: false,
      currSrc: '',
      previewSoundName: '',
      previewSoundSourceType: '',
      previewSoundAddress: '',
      drawerVisible: false,
      recordManagerVisible: false,
      currentSoundId: null,
      currentSoundName: '',
      list: [],
      summary: {
        total: 0,
        unreviewed: 0,
        verified: 0,
        high_risk: 0,
        search_only: 0,
        hidden: 0
      },
      options: {
        rights_statuses: [],
        verification_statuses: [],
        risk_levels: [],
        presets: []
      },
      filters: {
        search_name: '',
        verification_status: '',
        risk_level: '',
        preset_code: ''
      },
      page: {
        page_num: 1,
        page_size: 20,
        total: 0
      }
    }
  },
  computed: {
    /**
     * 将服务端汇总转换为顶部可点击的工作队列卡片。
     *
     * @returns {Array}
     */
    summaryCards() {
      return [
        { key: 'total', label: '资源总数', value: this.summary.total, description: '全部未删除资源', type: 'neutral' },
        { key: 'unreviewed', label: '待审核', value: this.summary.unreviewed, description: '保持当前展示', type: 'warning' },
        { key: 'verified', label: '已核验', value: this.summary.verified, description: '已有明确结论', type: 'success' },
        { key: 'high_risk', label: '高风险', value: this.summary.high_risk, description: '建议优先复核', type: 'danger' },
        { key: 'search_only', label: '仅搜索可见', value: this.summary.search_only, description: '可试听和下载', type: 'warning' },
        { key: 'hidden', label: '完全隐藏', value: this.summary.hidden, description: '全部入口关闭', type: 'danger' }
      ]
    },

    /**
     * 生成抽屉标题，让管理员始终知道正在维护哪个资源。
     *
     * @returns {String}
     */
    drawerTitle() {
      return this.currentSoundName ? `版权维护 · ${this.currentSoundName}` : '版权维护'
    },

    /**
     * 给工作台筛选器补充“高风险及以上”组合项。
     *
     * 该组合项只用于列表查询，不会进入版权编辑器，避免被误存为实际风险等级。
     *
     * @returns {Array}
     */
    riskFilterOptions() {
      return [
        ...(this.options.risk_levels || []),
        { value: 'high_or_critical', label: '高风险及以上' }
      ]
    }
  },
  /**
   * 初始化工作台选项和第一页数据。
   *
   * @returns {Promise<void>}
   */
  async created() {
    await this.loadOptions()
    await this.loadList(false)
    await this.openSoundFromRoute()
  },
  methods: {
    optionLabel,
    canPreview: canPreviewSound,
    verificationType: verificationTagType,
    riskType: riskTagType,
    presetType: exposurePresetTagType,
    /**
     * 从资源反馈等页面跳转过来时，直接打开指定音效的版权编辑器。
     *
     * 通过现有 read 接口读取资源名称，不依赖它是否刚好出现在当前分页中。
     * 没有 sound_id 查询参数时保持原工作台行为不变。
     *
     * @returns {Promise<void>}
     */
    async openSoundFromRoute() {
      const soundId = Number(this.$route.query.sound_id || 0)
      if (!soundId) return
      try {
        const response = await apis.soundRights.read({ sound_id: soundId })
        if (response.data.code !== 1) throw new Error(response.data.msg || '资源版权信息加载失败')
        const data = response.data.data || {}
        if (data.sound) this.editSound(data.sound)
      } catch (error) {
        this.$message.error(error.message || String(error))
      }
    },
    /**
     * 加载筛选框和状态展示所需的统一选项。
     *
     * @returns {Promise<void>}
     */
    async loadOptions() {
      try {
        const response = await apis.soundRights.options()
        if (response.data.code !== 1) throw new Error(response.data.msg || '版权选项加载失败')
        this.options = response.data.data || this.options
      } catch (error) {
        this.$message.error(error.message || String(error))
      }
    },

    /**
     * 按当前筛选和分页条件加载版权维护列表。
     *
     * @param {Boolean} resetPage 是否回到第一页
     * @returns {Promise<void>}
     */
    async loadList(resetPage = true) {
      if (resetPage) this.page.page_num = 1
      this.loading = true
      try {
        const response = await apis.soundRights.lists({
          ...this.filters,
          page_num: this.page.page_num,
          page_size: this.page.page_size
        })
        if (response.data.code !== 1) throw new Error(response.data.msg || '版权列表加载失败')
        const data = response.data.data || {}
        this.list = data.list || []
        this.page.total = Number(data.count || 0)
        this.summary = { ...this.summary, ...(data.summary || {}) }
      } catch (error) {
        this.$message.error(error.message || String(error))
      } finally {
        this.loading = false
      }
    },

    /**
     * 点击顶部汇总卡片时快速进入对应工作队列。
     *
     * @param {Object} card 汇总卡片
     * @returns {void}
     */
    applySummaryFilter(card) {
      this.filters.verification_status = ''
      this.filters.risk_level = ''
      this.filters.preset_code = ''
      if (card.key === 'unreviewed') this.filters.verification_status = 'unreviewed'
      if (card.key === 'verified') this.filters.verification_status = 'verified'
      if (card.key === 'high_risk') this.filters.risk_level = 'high_or_critical'
      if (card.key === 'search_only') this.filters.preset_code = 'search_only'
      if (card.key === 'hidden') this.filters.preset_code = 'hidden'
      this.loadList()
    },

    /**
     * 清空筛选条件并重新加载第一页。
     *
     * @returns {void}
     */
    resetFilters() {
      this.filters = {
        search_name: '',
        verification_status: '',
        risk_level: '',
        preset_code: ''
      }
      this.loadList()
    },

    /**
     * 从头播放当前资源，并在播放器下方显示脱敏后的来源地址。
     *
     * 每次点击都重新调用 Player.play，确保不会从上次暂停位置继续。
     *
     * @param {Object} sound 音效列表行
     * @returns {void}
     */
    playSound(sound) {
      const preview = buildSoundPreview(sound)
      if (!preview) {
        this.$message.warning('该音效没有可用的播放地址')
        return
      }

      this.previewSoundName = sound.name
      this.previewSoundSourceType = preview.sourceType
      this.previewSoundAddress = preview.maskedAddress
      this.$nextTick(() => {
        this.$refs.player.play({ src: preview.url, name: sound.name })
      })
    },

    /**
     * 打开可复用版权编辑器抽屉。
     *
     * @param {Object} sound 音效列表行
     * @returns {void}
     */
    editSound(sound) {
      this.currentSoundId = Number(sound.id)
      this.currentSoundName = sound.name
      this.drawerVisible = true
    },

    /**
     * 版权编辑器保存成功后刷新列表和汇总。
     *
     * @returns {void}
     */
    handleEditorSaved() {
      this.loadList(false)
    },

    /**
     * 公共版权档案保存后刷新资源列表，确保审核和风险摘要立即更新。
     *
     * @returns {void}
     */
    handleRecordSaved() {
      this.loadList(false)
    },

    /**
     * 修改每页条数并回到第一页。
     *
     * @param {Number} size 每页条数
     * @returns {void}
     */
    handlePageSizeChange(size) {
      this.page.page_size = size
      this.loadList()
    },

    /**
     * 切换页码并保持当前筛选条件。
     *
     * @param {Number} page 页码
     * @returns {void}
     */
    handlePageChange(page) {
      this.page.page_num = page
      this.loadList(false)
    }
  }
}
</script>

<style lang="scss" scoped>
.rights-workbench {
  padding-bottom: 24px;
}

.page-intro {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 15px 14px;
  padding: 18px 20px;
  border: 1px solid #d9ecff;
  border-radius: 10px;
  background: linear-gradient(135deg, #f4f9ff 0%, #fbfdff 100%);

  h2 {
    margin: 0 0 7px;
    font-size: 18px;
  }

  p {
    margin: 0;
    color: #606266;
    font-size: 13px;
  }
}

.intro-actions {
  display: flex;
  gap: 8px;
  margin-left: 16px;
}

.summary-row {
  margin: 0 15px 14px !important;
}

.summary-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 112px;
  padding: 14px 16px;
  border: 1px solid #e4e7ed;
  border-radius: 9px;
  color: #606266;
  background: #fff;
  text-align: left;
  cursor: pointer;

  &:hover {
    border-color: #a0cfff;
    box-shadow: 0 4px 14px rgba(31, 45, 61, .08);
  }

  strong {
    margin: 8px 0 4px;
    color: #303133;
    font-size: 26px;
  }

  small {
    color: #909399;
  }

  &.warning strong { color: #e6a23c; }
  &.success strong { color: #67c23a; }
  &.danger strong { color: #f56c6c; }
}

.filter-card,
.table-card {
  margin: 0 15px 14px;
}

.filters {
  display: grid;
  grid-template-columns: minmax(220px, 1.5fr) repeat(3, minmax(150px, 1fr)) auto auto;
  gap: 10px;
}

.preview-source {
  margin: 8px 0 12px;
  padding: 9px 12px;
  border-radius: 6px;
  color: #606266;
  background: #f5f7fa;
  font-size: 12px;

  .el-tag {
    margin: 0 8px;
  }
}

.sound-name {
  color: #303133;
  font-weight: 600;
}

.sound-meta,
.original-state {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
}

.pagination {
  margin-top: 16px;
  text-align: right;
}

.drawer-body {
  padding: 0 20px 28px;
}

@media (max-width: 1280px) {
  .filters {
    grid-template-columns: repeat(3, 1fr);
  }
}
</style>
